import { useEffect, useState } from "react";
import { useFormik } from "formik";
import styles from "./AddAppointment.module.scss";
import Button from "../../../../../../components/Button/Button";
import Chip from "../../../../../../components/Chip/Chip";
import SelectCustom from "../../../../../../components/SelectCustom/SelectCustom";
import SlotBookingForm from "../../../../../../components/Appointment/SlotBookingForm/SlotBookingForm";
import { fetchClients } from "../../../../../../api/client";
import { fetchRates } from "../../../../../../api/rates";
import { fetchServicesByRateId } from "../../../../../../api/services";
import { createAdminAppointment } from "../../../../../../api/appointment";

function AddAppointment({ onSuccess }) {
  const [clients, setClients] = useState([]);
  const [rates, setRates] = useState([]);
  const [services, setServices] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedRate, setSelectedRate] = useState("");
  const [selectedService, setSelectedService] = useState("");

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const getClientId = (client) => client.id || client.client_id;

  const getClientLabel = (client) => {
    const firstName = client.first_name || "";
    const lastName = client.last_name || "";
    const phoneNumber = client.phone_number || client.phoneNumber || "";

    return `${firstName} ${lastName} - ${phoneNumber}`;
  };

  const formik = useFormik({
    initialValues: {
      selectedDate: "",
      selectedSlot: "",
      services: [],
      sendEmail: false,
    },

    onSubmit: async (values) => {
      if (!selectedClientId) {
        setErrorMessage("Veuillez sélectionner une cliente.");
        return;
      }

      if (!values.selectedDate || !values.selectedSlot) {
        setErrorMessage("Veuillez sélectionner une date et un créneau.");
        return;
      }

      if (values.services.length === 0) {
        setErrorMessage("Veuillez ajouter au moins une prestation.");
        return;
      }

      const payload = {
        clientId: selectedClientId,
        selectedDate: values.selectedDate,
        selectedSlot: values.selectedSlot,
        services: values.services.map(({ rateId, serviceId }) => ({
          rateId,
          serviceId,
        })),
        prestations: values.services.map(
          ({ rateName, serviceTitle }) => `${rateName} : ${serviceTitle}`,
        ),
        sendEmail: values.sendEmail,
      };

      try {
        await createAdminAppointment(payload);

        setErrorMessage("");
        onSuccess();
      } catch (error) {
        setErrorMessage(
          error.response?.data?.message ||
            "Une erreur est survenue lors de la création du rendez-vous.",
        );
      }
    },
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [clientsResponse, ratesResponse] = await Promise.all([
          fetchClients(),
          fetchRates(),
        ]);
        console.log(clientsResponse.data);
        setClients(clientsResponse.data || []);
        setRates(ratesResponse.data || []);
      } catch (error) {
        console.error(
          "Erreur lors du chargement du formulaire de rendez-vous :",
          error,
        );

        setErrorMessage(
          "Impossible de charger les clientes ou les prestations.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadServices = async () => {
      if (!selectedRate) {
        setServices([]);
        return;
      }

      try {
        const response = await fetchServicesByRateId(selectedRate);

        if (!cancelled) {
          setServices(response.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Erreur lors du chargement des prestations :", error);

          setServices([]);
          setErrorMessage("Impossible de charger les prestations.");
        }
      }
    };

    loadServices();

    return () => {
      cancelled = true;
    };
  }, [selectedRate]);

  const handleClientChange = (event) => {
    setSelectedClientId(event.target.value);
    setErrorMessage("");
  };

  const handleRateChange = (event) => {
    setSelectedRate(event.target.value);
    setSelectedService("");
    setErrorMessage("");
  };

  const addService = () => {
    if (!selectedRate || !selectedService) {
      return;
    }

    const rate = rates.find(
      (item) => item.id.toString() === selectedRate.toString(),
    );

    const service = services.find(
      (item) => item.id.toString() === selectedService.toString(),
    );

    if (!rate || !service) {
      return;
    }

    formik.setFieldValue("services", [
      ...formik.values.services,
      {
        rateId: rate.id,
        serviceId: service.id,
        rateName: rate.name,
        serviceTitle: service.title,
      },
    ]);

    setSelectedRate("");
    setSelectedService("");
    setServices([]);
  };

  const removeService = (indexToRemove) => {
    formik.setFieldValue(
      "services",
      formik.values.services.filter((_, index) => index !== indexToRemove),
    );
  };

  if (loading) {
    return (
      <div className={styles.message}>
        <p>Chargement du formulaire...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.inputContainer}>
          <label htmlFor="clientId">Client</label>

          <SelectCustom
            id="clientId"
            value={selectedClientId}
            onChange={handleClientChange}
            placeholder="Sélectionner un client"
            options={clients.map((client) => ({
              value: getClientId(client),
              label: getClientLabel(client),
            }))}
          />
        </div>

        <div className={styles.inputContainer}>
          <label className={styles.title}>Ajouter une prestation</label>

          <div>
            <SelectCustom
              name="selectedRate"
              value={selectedRate}
              onChange={handleRateChange}
              placeholder="Sélectionner un type de prestation"
              options={rates.map((rate) => ({
                value: rate.id,
                label: rate.name,
              }))}
            />
          </div>

          {selectedRate && (
            <div>
              <SelectCustom
                name="selectedService"
                value={selectedService}
                onChange={(event) => setSelectedService(event.target.value)}
                placeholder="Sélectionner une prestation"
                options={services.map((service) => ({
                  value: service.id,
                  label: service.title,
                }))}
              />
            </div>
          )}

          {selectedRate && selectedService && (
            <div className={styles.btnContainer}>
              <Button
                type="button"
                color="var(--primary-color)"
                onClick={addService}
                style={{ padding: "7px 15px", fontSize: "0.85rem" }}
              >
                + Ajouter la prestation
              </Button>
            </div>
          )}
        </div>

        {formik.values.services.map((service, index) => (
          <Chip
            key={`${service.rateId}-${service.serviceId}-${index}`}
            label={`${service.rateName} - ${service.serviceTitle}`}
            onDelete={() => removeService(index)}
          />
        ))}

        <div className={styles.inputContainer}>
          <label>Choisir un créneau</label>

          <SlotBookingForm
            selectedDate={formik.values.selectedDate}
            selectedSlot={formik.values.selectedSlot}
            onDateChange={(date) => formik.setFieldValue("selectedDate", date)}
            onSlotChange={(slot) => formik.setFieldValue("selectedSlot", slot)}
          />
        </div>

        <label className={styles.emailOption}>
          <input
            type="checkbox"
            checked={formik.values.sendEmail}
            onChange={(event) =>
              formik.setFieldValue("sendEmail", event.target.checked)
            }
          />
          Envoyer une confirmation par email au client
        </label>

        <div className={styles.btnContainer}>
          <Button
            type="submit"
            color="var(--secondary-color)"
            disabled={formik.isSubmitting}
            style={{ padding: "7px 15px" }}
          >
            {formik.isSubmitting
              ? "Création en cours..."
              : "Créer le rendez-vous"}
          </Button>
        </div>

        <div className={styles.message}>
          {errorMessage && (
            <p className={styles.errorMessage}>{errorMessage}</p>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddAppointment;
